import { useState } from "react";
import { Check, Copy, Link2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const TTL = [30, 90, 0];

/**
 * 2026-06 · 生成产品分享链接（仅管理员）。一次生成两种用法：
 * 普通链接 /p/{token} 与网页内嵌 iframe 代码（同一 token 加 ?embed=1）。
 */
export function ProductLinkDialog({ open, onOpenChange, itemNumbers }) {
  const { t } = useLanguage();
  const [title, setTitle] = useState("");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");

  const reset = () => { setResult(null); setTitle(""); setDays(30); setCopied(""); };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/product-links", { item_numbers: itemNumbers, title, days });
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement("textarea");
      el.value = text; document.body.appendChild(el); el.select();
      document.execCommand("copy"); el.remove();
    }
    setCopied(key);
    toast.success(t("plink.copied"));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg" data-testid="product-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Link2 size={16} /> {t("plink.title")}
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground" data-testid="plink-count">
              {t("plink.selected").replace("{n}", itemNumbers.length)}
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("plink.titleLabel")}</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder={t("plink.titlePlaceholder")} className="h-9"
                data-testid="plink-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("plink.validity")}</Label>
              <div className="flex gap-1.5">
                {TTL.map((d) => (
                  <button key={d} type="button" onClick={() => setDays(d)}
                    className={`h-9 px-3 rounded-md border text-xs transition-colors ${days === d ? "border-brand bg-brand/10 text-brand font-semibold" : "border-border"}`}
                    data-testid={`plink-days-${d}`}>
                    {d === 0 ? t("plink.forever") : t("plink.days").replace("{n}", d)}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">{t("plink.rulesHint")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <Row label={t("plink.shareLink")} value={result.url} testId="plink-url"
              copied={copied === "url"} onCopy={() => copy("url", result.url)} />
            <Row label={t("plink.embedCode")} value={result.embed_code} testId="plink-embed"
              copied={copied === "embed"} onCopy={() => copy("embed", result.embed_code)} multiline />
            <p className="text-[11px] text-muted-foreground" data-testid="plink-result-meta">
              {t("plink.resultMeta").replace("{n}", result.count)
                .replace("{exp}", result.expires_at ? new Date(result.expires_at).toLocaleDateString() : t("plink.forever"))}
            </p>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <Button onClick={generate} disabled={busy || !itemNumbers.length}
              className="bg-brand hover:bg-brand-hover" data-testid="plink-generate-button">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} {t("plink.generate")}
            </Button>
          ) : (
            <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} data-testid="plink-close-button">
              {t("common.done")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ label, value, onCopy, copied, testId, multiline }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    <div className="flex items-start gap-1.5">
      {multiline ? (
        <textarea readOnly value={value} rows={3} data-testid={`${testId}-value`}
          className="flex-1 rounded-md border border-border bg-secondary/40 p-2 font-mono text-[11px] resize-none" />
      ) : (
        <Input readOnly value={value} className="flex-1 h-9 font-mono text-xs" data-testid={`${testId}-value`} />
      )}
      <Button variant="outline" size="sm" className="h-9 px-2.5" onClick={onCopy} data-testid={`${testId}-copy`}>
        {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
      </Button>
    </div>
  </div>
);
